import { createFileRoute, useNavigate, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, MapPin, Zap, X, Mountain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { MODALITIES, PACES, relativeMinutes, paceLabel, type Modality, type Pace } from "@/lib/modality";
import { RideMap } from "@/components/RideMap";
import { isCompatible, type CompatibilityInput } from "@/lib/geo";

interface PortStart {
  id: string; name: string; slope_side: string | null; category: string | null;
  province: string; comarca: string | null; length_km: number | null;
  elevation_gain_m: number | null; avg_gradient: number | null; surface: string;
  start_municipality: string | null; start_lat: number | null; start_lng: number | null;
}

const CATEGORY_COLORS: Record<string, string> = {
  HC: "#ef4444",
  "1": "#f97316",
  "2": "#F5C518",
  "3": "#4caf7d",
  "4": "#3b82f6",
};

const normalize = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const fmtNum = (n: number | null, decimals = 0) =>
  n == null ? null : n.toLocaleString("es-ES", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export const Route = createFileRoute("/create")({
  component: CreateRide,
});

interface Geo { lat: number; lng: number; address: string; officialId?: string }

function CreateRide() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/create" }) as { portId?: string };
  const [modality, setModality] = useState<Modality | null>(null);
  const [pace, setPace] = useState<Pace | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [time, setTime] = useState(() => {
    const d = new Date(Date.now() + 60 * 60_000);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Geo[]>([]);
  const [location, setLocation] = useState<Geo | null>(null);
  const [km, setKm] = useState(40);
  const [duration, setDuration] = useState(90);
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<"official" | "free">("official");
  const [portList, setPortList] = useState<PortStart[]>([]);
  const [officialQuery, setOfficialQuery] = useState("");
  const [selectedPort, setSelectedPort] = useState<PortStart | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [match, setMatch] = useState<CompatibilityInput & { start_address: string | null; estimated_km: number | null; estimated_duration_min: number | null; createdId: string } | null>(null);
  const [adminClubs, setAdminClubs] = useState<{ id: string; name: string }[]>([]);
  const [isClubRide, setIsClubRide] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState<string | null>(null);

  useEffect(() => { if (!loading && !user) navigate({ to: "/auth" }); }, [loading, user, navigate]);

  useEffect(() => {
    const load = async () => {
      // Wait for the session to be available so the RLS-filtered query returns rows
      await supabase.auth.getSession();
      const { data, error } = await supabase
        .from("ports")
        .select("id, name, slope_side, category, province, comarca, length_km, elevation_gain_m, avg_gradient, surface, start_municipality, start_lat, start_lng")
        .eq("is_active", true)
        .eq("is_departure_point", true)
        .not("start_lat", "is", null)
        .not("start_lng", "is", null)
        .order("name", { ascending: true });
      if (error) {
        console.error("Error loading ports:", error);
        return;
      }
      const list = (data as PortStart[]) || [];
      setPortList(list);
      if (list.length === 0) setMode("free");
    };
    load();
  }, []);

  // Preselect a port when opening the form from the map popup (e.g. /create?portId=...)
  useEffect(() => {
    const portId = search.portId;
    if (!portId || portList.length === 0) return;
    const port = portList.find((p) => p.id === portId);
    if (!port) return;
    setMode("official");
    setSelectedPort(port);
    if (port.start_lat != null && port.start_lng != null) {
      setLocation({
        lat: port.start_lat,
        lng: port.start_lng,
        address: `Inicio ${port.name} · ${port.start_municipality ?? ""}`,
        officialId: port.id,
      });
    }
  }, [search.portId, portList]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("club_members")
        .select("club_id, clubs:club_id(id, name)")
        .eq("user_id", user.id)
        .eq("role", "club_admin")
        .eq("status", "approved");
      const clubs = ((data as any[]) || [])
        .map((r) => r.clubs)
        .filter((c): c is { id: string; name: string } => !!c);
      setAdminClubs(clubs);
      if (clubs.length === 1) setSelectedClubId(clubs[0].id);
    })();
  }, [user?.id]);

  useEffect(() => {
    if (location || query.length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=es&q=${encodeURIComponent(query)}`,
          { headers: { "Accept-Language": "es" } }
        );
        const data = await res.json();
        setResults(
          data.map((d: any) => ({
            lat: parseFloat(d.lat),
            lng: parseFloat(d.lon),
            address: d.display_name as string,
          }))
        );
      } catch {/* ignore */}
    }, 400);
    return () => clearTimeout(t);
  }, [query, location]);

  const submit = async () => {
    if (!modality || !pace || !location) {
      toast.error(t("create.completeFields"));
      return;
    }
    if (isClubRide && !selectedClubId) {
      toast.error("Elige un club");
      return;
    }
    setSubmitting(true);
    try {
      const base = new Date(selectedDate);
      const [h, m] = time.split(":").map(Number);
      base.setHours(h, m, 0, 0);

      const { data, error } = await supabase
        .from("rides")
        .insert({
          creator_id: user!.id,
          modality, pace,
          start_time: base.toISOString(),
          start_lat: location.lat,
          start_lng: location.lng,
          start_address: location.address,
          estimated_km: km,
          estimated_duration_min: duration,
          description: description || null,
          club_id: isClubRide && selectedClubId ? selectedClubId : null,
          port_id: selectedPort?.id ?? null,
        } as any)
        .select("id")
        .single();
      if (error) throw error;
      await supabase.from("ride_participants").insert({ ride_id: data!.id, user_id: user!.id });
      toast.success(t("create.rideCreated"));

      // Look for compatible rides
      const newRide: CompatibilityInput = {
        id: data!.id,
        modality, pace,
        start_time: base.toISOString(),
        start_lat: location.lat,
        start_lng: location.lng,
      };
      const { data: candidates } = await supabase
        .from("rides")
        .select("id, modality, pace, start_time, start_lat, start_lng, start_address, estimated_km, estimated_duration_min, creator_id")
        .eq("status", "active")
        .eq("modality", modality)
        .eq("pace", pace)
        .neq("id", data!.id)
        .neq("creator_id", user!.id);
      const compatible = (candidates || []).find((c: any) => isCompatible(newRide, c));
      if (compatible) {
        setMatch({ ...(compatible as any), createdId: data!.id });
        return;
      }
      navigate({ to: "/ride/$rideId", params: { rideId: data!.id } });
    } catch (err: any) {
      toast.error(err.message || t("create.couldNotCreate"));
    } finally { setSubmitting(false); }
  };

  const localeMap: Record<string, string> = { es: "es-ES", ca: "ca-ES", en: "en-US" };
  const dateLocale = localeMap[i18n.language] ?? "es-ES";

  return (
    <div className="min-h-screen bg-background pb-32 fade-in">
      <header className="sticky top-0 z-20 glass-panel flex items-center gap-3 px-4 py-3 pt-[max(env(safe-area-inset-top),0.75rem)]">
        <Link to="/map" className="rounded-full bg-surface p-2"><ArrowLeft className="h-5 w-5" /></Link>
        <h1 className="font-display text-2xl">{t("create.title")}</h1>
      </header>

      <div className="space-y-6 px-5 py-6">
        <Section title={t("create.modality")}>
          <div className="grid grid-cols-2 gap-2">
            {MODALITIES.map((m) => {
              const Icon = m.Icon;
              const selected = modality === m.value;
              return (
                <button
                  key={m.value}
                  onClick={() => setModality(m.value)}
                  className={`rounded-2xl border-2 py-4 flex flex-col items-center gap-1.5 transition-all ${
                    selected
                      ? "border-primary bg-surface shadow-[0_0_12px_rgba(245, 197, 24,0.3)]"
                      : "border-border bg-surface"
                  }`}
                >
                  <Icon className={`h-6 w-6 ${selected ? "text-primary" : "text-white"}`} strokeWidth={1.8} />
                  <div className={`font-display text-sm font-bold uppercase tracking-wide ${selected ? "text-primary" : "text-white"}`}>{m.label}</div>
                </button>
              );
            })}
          </div>
        </Section>

        <Section title={t("create.pace")}>
          <div className="grid grid-cols-2 gap-2">
            {PACES.map((p) => {
              const Icon = p.Icon;
              const selected = pace === p.value;
              return (
                <button
                  key={p.value}
                  onClick={() => setPace(p.value)}
                  className={`rounded-2xl border-2 py-4 flex flex-col items-center gap-1.5 transition-all ${
                    selected
                      ? "border-primary bg-surface shadow-[0_0_12px_rgba(245, 197, 24,0.3)]"
                      : "border-border bg-surface"
                  }`}
                >
                  <Icon className={`h-5 w-5 ${selected ? "text-primary" : "text-white/80"}`} strokeWidth={2} />
                  <div className={`font-display text-base font-bold ${selected ? "text-primary" : "text-white"}`}>{p.label}</div>
                  <div className="text-[11px] text-muted-foreground">{p.hint}</div>
                </button>
              );
            })}
          </div>
        </Section>

        <Section title={t("create.dayOfRide")}>
          <DayStrip selected={selectedDate} onSelect={setSelectedDate} />
          <div className="mt-3 flex items-center gap-2">
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm"
            />
            <div className="flex-1 text-xs text-muted-foreground">
              {t("create.departureOn")}{" "}
              <span className="font-semibold text-primary">
                {selectedDate.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long" })}
              </span>{" "}
              {t("create.at")} <span className="font-semibold text-primary">{time}</span>
            </div>
          </div>
        </Section>

        <Section title={t("create.departurePoint")}>
          <div className="mb-2 grid grid-cols-2 gap-1 rounded-full bg-surface p-1">
            {(["official", "free"] as const).map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setLocation(null); setQuery(""); setOfficialQuery(""); setSelectedPort(null); }}
                className={`rounded-full py-2 text-xs font-medium transition-colors ${
                  mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                {m === "official" ? t("create.officialPoint") : t("create.freeAddress")}
              </button>
            ))}
          </div>

          {mode === "official" ? (
            <>
              <div className="relative">
                <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <input
                  value={selectedPort ? selectedPort.name : officialQuery}
                  onChange={(e) => { setOfficialQuery(e.target.value); setLocation(null); setSelectedPort(null); }}
                  placeholder={t("create.searchOfficial")}
                  className="w-full rounded-2xl border border-border bg-surface py-3 pl-9 pr-3 text-sm outline-none focus:border-primary"
                />
              </div>
              {!location && (
                <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-border bg-surface">
                  {portList
                    .filter((d) => {
                      const q = normalize(officialQuery.trim());
                      if (!q) return true;
                      return [d.name, d.start_municipality, d.comarca, d.province]
                        .filter(Boolean)
                        .some((s) => normalize(s as string).includes(q));
                    })
                    .sort((a, b) => {
                      // Gravel/MTB: primero superficies gravel o mixto (solo orden, no filtro)
                      if (modality === "gravel" || modality === "mtb") {
                        const pref = (s: string) => (s === "gravel" || s === "mixto" ? 0 : 1);
                        const diff = pref(a.surface) - pref(b.surface);
                        if (diff !== 0) return diff;
                      }
                      return a.name.localeCompare(b.name);
                    })
                    .map((d) => {
                      const catColor = CATEGORY_COLORS[d.category ?? ""] ?? "#888888";
                      const dataLine = [
                        d.length_km != null ? `${fmtNum(d.length_km, 1)} km` : null,
                        d.elevation_gain_m != null ? `${fmtNum(d.elevation_gain_m)} m D+` : null,
                        d.avg_gradient != null ? `${fmtNum(d.avg_gradient, 1)}%` : null,
                        d.surface ? d.surface.charAt(0).toUpperCase() + d.surface.slice(1) : null,
                      ].filter(Boolean).join(" · ");
                      const placeLine = [d.start_municipality, d.comarca].filter(Boolean).join(" · ");
                      return (
                        <button
                          key={d.id}
                          onClick={() => {
                            setSelectedPort(d);
                            setLocation({
                              lat: d.start_lat!,
                              lng: d.start_lng!,
                              address: `Inicio ${d.name} · ${d.start_municipality ?? ""}`.trim(),
                              officialId: d.id,
                            });
                            setOfficialQuery("");
                          }}
                          className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left last:border-0 hover:bg-surface-elevated"
                        >
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                            style={{ backgroundColor: `${catColor}26`, color: catColor }}
                          >
                            <Mountain className="h-3.5 w-3.5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate text-sm font-medium text-white">{d.name}</span>
                              {d.slope_side && (
                                <span className="truncate text-[10px] text-muted-foreground">{d.slope_side}</span>
                              )}
                              {d.category && (
                                <span
                                  className="ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                                  style={{ backgroundColor: `${catColor}26`, color: catColor }}
                                >
                                  {d.category}
                                </span>
                              )}
                            </div>
                            {dataLine && <div className="truncate text-[11px] text-white/60">{dataLine}</div>}
                            {placeLine && <div className="truncate text-[11px] text-muted-foreground">{placeLine}</div>}
                          </div>
                        </button>
                      );
                    })}
                  {portList.length === 0 && (
                    <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                      {t("create.noOfficialNearby")}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="relative">
                <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <input
                  value={location ? location.address.split(",").slice(0, 2).join(", ") : query}
                  onChange={(e) => { setQuery(e.target.value); setLocation(null); }}
                  placeholder={t("create.searchAddress")}
                  className="w-full rounded-2xl border border-border bg-surface py-3 pl-9 pr-3 text-sm outline-none focus:border-primary"
                />
              </div>
              {!location && results.length > 0 && (
                <div className="mt-2 overflow-hidden rounded-xl border border-border bg-surface">
                  {results.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => { setLocation(r); setResults([]); setQuery(""); }}
                      className="block w-full border-b border-border px-3 py-2 text-left text-xs last:border-0 hover:bg-surface-elevated"
                    >
                      {r.address}
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-2 text-[11px] text-muted-foreground">
                {t("create.proposeHint")}
              </p>
            </>
          )}
          {location && (
            <div className="mt-2 overflow-hidden rounded-2xl border border-border">
              <div style={{ height: 100 }}>
                <RideMap
                  rides={[{ id: "preview", modality: modality ?? "road", lat: location.lat, lng: location.lng }]}
                  center={[location.lat, location.lng]}
                  interactive={false}
                  zoom={14}
                />
              </div>
              {selectedPort && (
                <div className="flex items-center gap-3 border-t border-border bg-surface px-3 py-2.5">
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                    style={{
                      backgroundColor: `${CATEGORY_COLORS[selectedPort.category ?? ""] ?? "#888888"}26`,
                      color: CATEGORY_COLORS[selectedPort.category ?? ""] ?? "#888888",
                    }}
                  >
                    <Mountain className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-white">{selectedPort.name}</span>
                      {selectedPort.category && (
                        <span
                          className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                          style={{
                            backgroundColor: `${CATEGORY_COLORS[selectedPort.category] ?? "#888888"}26`,
                            color: CATEGORY_COLORS[selectedPort.category] ?? "#888888",
                          }}
                        >
                          {selectedPort.category}
                        </span>
                      )}
                    </div>
                    <div className="truncate text-[11px] text-white/60">
                      {[
                        selectedPort.length_km != null ? `${fmtNum(selectedPort.length_km, 1)} km` : null,
                        selectedPort.elevation_gain_m != null ? `${fmtNum(selectedPort.elevation_gain_m)} m D+` : null,
                        selectedPort.avg_gradient != null ? `${fmtNum(selectedPort.avg_gradient, 1)}%` : null,
                        selectedPort.surface ? selectedPort.surface.charAt(0).toUpperCase() + selectedPort.surface.slice(1) : null,
                      ].filter(Boolean).join(" · ")}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {[selectedPort.start_municipality, selectedPort.comarca].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>

        <div className="grid grid-cols-2 gap-3">
          <Stepper label={t("create.km")} value={km} setValue={setKm} step={5} min={5} suffix="km" />
          <Stepper label={t("create.duration")} value={duration} setValue={setDuration} step={15} min={15} suffix="min" />
        </div>

        <Section title={t("create.description")}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder={t("create.descriptionPlaceholder")}
            className="w-full resize-none rounded-2xl border border-border bg-surface p-3 text-sm outline-none focus:border-primary"
          />
        </Section>

        {adminClubs.length > 0 && (
          <Section title={adminClubs.length === 1 ? `¿Es una salida de ${adminClubs[0].name}?` : "¿Es una salida de club?"}>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-surface px-4 py-3">
              <span className="text-sm">{isClubRide ? "Sí" : "No"}</span>
              <button
                type="button"
                onClick={() => {
                  const next = !isClubRide;
                  setIsClubRide(next);
                  if (!next) {
                    if (adminClubs.length > 1) setSelectedClubId(null);
                  } else if (adminClubs.length === 1) {
                    setSelectedClubId(adminClubs[0].id);
                  }
                }}
                className={`relative h-6 w-11 rounded-full transition-colors ${isClubRide ? "bg-primary" : "bg-surface-elevated"}`}
                aria-pressed={isClubRide}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${isClubRide ? "left-[1.375rem]" : "left-0.5"}`} />
              </button>
            </div>
            {isClubRide && adminClubs.length > 1 && (
              <select
                value={selectedClubId ?? ""}
                onChange={(e) => setSelectedClubId(e.target.value || null)}
                className="mt-2 w-full rounded-2xl border border-border bg-surface px-3 py-3 text-sm outline-none focus:border-primary"
              >
                <option value="">Elige un club…</option>
                {adminClubs.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </Section>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 glass-panel p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <p className="mb-2 text-center text-[11px] leading-snug text-muted-foreground">
          {t("create.disclaimer")}
        </p>
        <button
          onClick={submit}
          disabled={submitting}
          className="w-full rounded-full bg-primary py-4 font-semibold text-primary-foreground disabled:opacity-50"
        >
          {submitting ? t("create.submitting") : t("create.submit")}
        </button>
      </div>

      {match && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm fade-in">
          <div className="w-full max-w-md rounded-t-3xl border-t border-x border-border bg-[#1a1a1a] p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] sheet-up">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" />
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" fill="currentColor" />
                <h2 className="font-display text-xl">{t("create.compatibleFound")}</h2>
              </div>
              <button onClick={() => navigate({ to: "/ride/$rideId", params: { rideId: match.createdId } })} className="rounded-full bg-surface-elevated p-1.5">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 rounded-2xl border border-border bg-surface p-4">
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-full bg-primary/15 px-2.5 py-1 font-semibold uppercase tracking-wider text-primary">
                  {MODALITIES.find((m) => m.value === match.modality)?.label}
                </span>
                <span className="rounded-full bg-surface-elevated px-2.5 py-1">{paceLabel(match.pace)}</span>
              </div>
              <div className="mt-2 font-display text-lg">{relativeMinutes(match.start_time)}</div>
              <div className="truncate text-xs text-muted-foreground">{match.start_address ?? t("create.departurePoint")}</div>
              {(match.estimated_km || match.estimated_duration_min) && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {match.estimated_km ? `${match.estimated_km} km` : ""}
                  {match.estimated_km && match.estimated_duration_min ? " · " : ""}
                  {match.estimated_duration_min ? `${match.estimated_duration_min} min` : ""}
                </div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => navigate({ to: "/ride/$rideId", params: { rideId: match.createdId } })}
                className="press-scale rounded-xl border border-border bg-surface py-3 text-sm font-medium"
              >
                {t("create.keepMine")}
              </button>
              <button
                onClick={() => navigate({ to: "/ride/$rideId", params: { rideId: match.id } })}
                className="press-scale rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground"
              >
                {t("create.viewCompatible")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      {children}
    </div>
  );
}

function Stepper({ label, value, setValue, step, min, suffix }: {
  label: string; value: number; setValue: (n: number) => void; step: number; min: number; suffix: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 flex items-center justify-between">
        <button onClick={() => setValue(Math.max(min, value - step))} className="rounded-full bg-surface-elevated p-2">
          <Minus className="h-4 w-4" />
        </button>
        <div className="font-display text-2xl">{value}<span className="ml-1 text-xs text-muted-foreground">{suffix}</span></div>
        <button onClick={() => setValue(value + step)} className="rounded-full bg-surface-elevated p-2">
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function DayStrip({ selected, onSelect }: { selected: Date; onSelect: (d: Date) => void }) {
  const { t } = useTranslation();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(today); d.setDate(today.getDate() + i); return d;
  });
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {days.map((d) => {
        const isToday = sameDay(d, today);
        const isSelected = sameDay(d, selected);
        const label = isToday ? t("create.today") : t(`create.weekdays.${d.getDay()}` as any);
        return (
          <button
            key={d.toISOString()}
            onClick={() => onSelect(d)}
            className={`press-scale flex shrink-0 flex-col items-center gap-0.5 rounded-2xl px-3 py-2 transition-colors ${
              isSelected
                ? "bg-[#F5C518] text-black font-bold"
                : isToday
                ? "bg-[#1a1a1a] text-white border-2 border-[#F5C518]"
                : "bg-[#1a1a1a] text-white border border-[#2a2a2a]"
            }`}
            style={{ minWidth: 52 }}
          >
            <span className="text-[10px] font-semibold tracking-wider">{label}</span>
            <span className="font-display text-lg leading-none">{d.getDate()}</span>
          </button>
        );
      })}
    </div>
  );
}
